import { ActionType } from "@sp-crm/core";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ChoiceTileOption, ChoiceTiles } from "components/ui/choice-tiles";
import { InlineBanner } from "components/ui/inline-banner";
import {
    RegionVisibility,
    RegionVisibilityTree,
} from "components/ui/region-visibility-tree";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { useWorkflowEntityTypes } from "store/selectors/bridge";
import { useCrmMode } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { getWorkflowStatusIds } from "../manage-workflows-helper";
import { WorkflowTriggerEditor } from "../workflow-trigger-editor";
import { WorkflowTriggerSummary } from "../workflow-trigger-summary";
import { workflowEventProperties } from "./builder-analytics";
import { workflowEntityTypeLabel } from "./builder-helper";
import { EntityTypeIcon } from "./entity-type-icon";
import { TriggerKindIcon } from "./trigger-kind-icon";
import { WizardStepProps } from "./wizard-types";
import {
    availableTriggersIncluding,
    blueprintEntityTypes,
    seededStatusGateCleared,
    triggerOwnsStatusGate,
    withEntityType,
    withTrigger,
    WorkflowTriggerKind,
} from "./workflow-blueprint";
import { WorkflowStopRules } from "./workflow-stop-rules";

export const WizardBasics: React.FC<WizardStepProps> = props => {
    const {
        workflow,
        editable,
        isDraft,
        canEditStructure,
        onChangeTrigger,
        editPayload,
        blueprint,
    } = props;
    const { mode } = useCrmMode();
    const workflowEntityTypes = useWorkflowEntityTypes();

    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint.blueprint),
        [workflow, blueprint.blueprint],
    );

    const commitName = React.useCallback(
        (name: string) => {
            handleEvent("workflow-builder-edit", { ...eventProperties, field: "name" });
            editPayload(payload => ({ ...payload, name }));
        },
        [editPayload, eventProperties],
    );

    const commitRegionVisibility = React.useCallback(
        (regions: RegionVisibility[]) => {
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "regionVisibility",
            });
            editPayload(payload => ({ ...payload, regionVisibility: { regions } }));
        },
        [editPayload, eventProperties],
    );

    const entityTypeOptions = React.useMemo<ChoiceTileOption<AdvancedSearchEntityType>[]>(
        () =>
            blueprintEntityTypes(workflowEntityTypes).map(entityType => ({
                key: entityType,
                title: workflowEntityTypeLabel(entityType, mode),
                icon: <EntityTypeIcon attachTo={entityType} className="w-5 h-5" />,
            })),
        [workflowEntityTypes, mode],
    );

    const triggerOptions = React.useMemo<ChoiceTileOption<WorkflowTriggerKind>[]>(() => {
        if (!blueprint.blueprint) {
            return [];
        }
        return availableTriggersIncluding(
            workflow.attachTo,
            blueprint.context,
            blueprint.blueprint.triggerKind,
        ).map(trigger => ({
            key: trigger.kind,
            title: trigger.title,
            description: trigger.description,
            icon: <TriggerKindIcon kind={trigger.kind} className="w-5 h-5" />,
        }));
    }, [blueprint.blueprint, blueprint.context, workflow.attachTo]);

    const statusGateCameWithTrigger = blueprint.blueprint
        ? triggerOwnsStatusGate(blueprint.blueprint)
        : false;

    const statusGateWasCleared = blueprint.blueprint
        ? seededStatusGateCleared(blueprint.blueprint, getWorkflowStatusIds(workflow))
        : false;

    const neverRunsWarningReported = React.useRef(false);

    React.useEffect(() => {
        if (!statusGateWasCleared) {
            neverRunsWarningReported.current = false;
            return;
        }
        if (neverRunsWarningReported.current) {
            return;
        }
        neverRunsWarningReported.current = true;
        handleEvent("workflow-builder-never-runs-warning", eventProperties);
    }, [statusGateWasCleared, eventProperties]);

    const currentTrigger = React.useMemo(
        () =>
            triggerOptions.find(
                option => option.key === blueprint.blueprint?.triggerKind,
            ),
        [triggerOptions, blueprint.blueprint],
    );

    const handleEntityTypeSelect = React.useCallback(
        (entityType: AdvancedSearchEntityType) => {
            if (!blueprint.blueprint) {
                return;
            }
            const target = withEntityType(blueprint.blueprint, entityType);
            if (target) {
                blueprint.onApply(target);
            }
        },
        [blueprint],
    );

    const handleTriggerSelect = React.useCallback(
        (triggerKind: WorkflowTriggerKind) => {
            if (!blueprint.blueprint) {
                return;
            }
            const target = withTrigger(blueprint.blueprint, triggerKind);
            if (target) {
                blueprint.onApply(target);
            }
        },
        [blueprint],
    );

    return (
        <div className="space-y-6">
            <div>
                <AutosavingInput
                    label="Name"
                    disabled={!editable}
                    initial={workflow.name || ""}
                    onCommit={commitName}
                />
            </div>
            <div>
                <h3 className="font-medium text-gray-900">Record Type</h3>
                <div className="mt-3">
                    {canEditStructure && entityTypeOptions.length > 1 ? (
                        <ChoiceTiles
                            options={entityTypeOptions}
                            columns={2}
                            value={workflow.attachTo}
                            onSelect={handleEntityTypeSelect}
                        />
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-700">
                            <EntityTypeIcon
                                attachTo={workflow.attachTo}
                                className="w-4 h-4 text-gray-500"
                            />
                            {workflowEntityTypeLabel(workflow.attachTo, mode)}
                        </span>
                    )}
                    {editable && !isDraft && blueprint.blueprint ? (
                        <p className="text-xs text-gray-400 mt-2">
                            Unpublish this workflow to change its record type or trigger.
                        </p>
                    ) : null}
                </div>
            </div>
            <div>
                <h3 className="font-medium text-gray-900">Starts When</h3>
                {canEditStructure ? (
                    <div className="mt-3">
                        <ChoiceTiles
                            options={triggerOptions}
                            columns={2}
                            value={blueprint.blueprint.triggerKind}
                            onSelect={handleTriggerSelect}
                        />
                    </div>
                ) : (
                    <div className="mt-3">
                        {currentTrigger ? (
                            <>
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-700">
                                    <TriggerKindIcon
                                        kind={currentTrigger.key}
                                        className="w-4 h-4 text-gray-500"
                                    />
                                    {currentTrigger.title}
                                </span>
                                {currentTrigger.description ? (
                                    <p className="mt-2 text-sm text-gray-500">
                                        {currentTrigger.description}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <p className="text-sm text-gray-700">
                                <WorkflowTriggerSummary workflow={workflow} />
                            </p>
                        )}
                    </div>
                )}
                <div className="mt-3 space-y-2">
                    <WorkflowTriggerEditor
                        workflow={workflow}
                        onChange={onChangeTrigger}
                    />
                    {statusGateCameWithTrigger && !statusGateWasCleared ? (
                        <p className="text-sm text-gray-500">
                            These statuses came with the trigger. Change them if you want
                            the workflow to run for different ones.
                        </p>
                    ) : null}
                    {statusGateWasCleared ? (
                        <InlineBanner type="warning">
                            This trigger only runs for the statuses listed above, and none
                            are selected, so the workflow will never run. Choose at least
                            one status.
                        </InlineBanner>
                    ) : null}
                </div>
            </div>
            <WorkflowStopRules workflow={workflow} blueprint={blueprint.blueprint} />
            <RegionVisibilityTree
                actionType={ActionType.CreateWorkflow}
                editable={editable}
                regionVisibility={
                    workflow.regionVisibility?.regions?.map(r => ({
                        regionId: r.regionId,
                        mode: r.mode,
                    })) || []
                }
                onChange={commitRegionVisibility}
                entityName="workflow"
            />
        </div>
    );
};
